import React, { useState, useEffect, useRef } from 'react';
import { FaPhone, FaKey, FaLock, FaEye, FaEyeSlash, FaTimes, FaCheckCircle, FaSpinner } from 'react-icons/fa';
import * as authService from '../services/authService';

// ─── Step Indicator ───────────────────────────────────────────────────────────
const StepIndicator = ({ currentStep }) => {
    const steps = [
        { id: 1, label: 'Phone' },
        { id: 2, label: 'Verify OTP' },
        { id: 3, label: 'New Password' },
    ];

    return (
        <div className="flex items-center justify-center mb-8">
            {steps.map((step, i) => (
                <React.Fragment key={step.id}>
                    <div className="flex flex-col items-center">
                        <div
                            className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${currentStep > step.id
                                    ? 'bg-green-500 text-white'
                                    : currentStep === step.id
                                        ? 'bg-primary-blue text-white shadow-lg shadow-blue-200'
                                        : 'bg-gray-100 text-gray-400'
                                }`}
                        >
                            {currentStep > step.id ? <FaCheckCircle className="w-4 h-4" /> : step.id}
                        </div>
                        <span
                            className={`mt-1 text-xs font-medium ${currentStep >= step.id ? 'text-gray-700' : 'text-gray-400'
                                }`}
                        >
                            {step.label}
                        </span>
                    </div>
                    {i < steps.length - 1 && (
                        <div
                            className={`flex-1 h-0.5 mx-2 mb-4 transition-all duration-300 ${currentStep > step.id ? 'bg-green-500' : 'bg-gray-200'
                                }`}
                        />
                    )}
                </React.Fragment>
            ))}
        </div>
    );
};

// ─── Main Modal ───────────────────────────────────────────────────────────────
const ForgotPasswordModal = ({ isOpen, onClose }) => {
    const [step, setStep] = useState(1); // 1: phone, 2: otp, 3: new password
    const [phone, setPhone] = useState('');
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [resendCountdown, setResendCountdown] = useState(0);

    const otpRefs = useRef([]);
    const timerRef = useRef(null);

    // Start countdown when OTP step starts
    useEffect(() => {
        if (step === 2) startResendTimer();
        return () => clearInterval(timerRef.current);
        // eslint-disable-next-line
    }, [step]);

    const startResendTimer = () => {
        setResendCountdown(60);
        clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setResendCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const resetModal = () => {
        setStep(1);
        setPhone('');
        setOtp(['', '', '', '', '', '']);
        setNewPassword('');
        setConfirmPassword('');
        setError('');
        setSuccess('');
        setIsLoading(false);
        setResendCountdown(0);
        clearInterval(timerRef.current);
    };

    const handleClose = () => {
        resetModal();
        onClose();
    };

    // ── Step 1: Send OTP ──────────────────────────────────────────────────────
    const handleSendOtp = async (e) => {
        e.preventDefault();
        setError('');

        if (!/^\d{10}$/.test(phone)) {
            setError('Please enter a valid 10-digit phone number.');
            return;
        }

        setIsLoading(true);
        try {
            const res = await authService.sendPhoneOtp(phone);
            if (res.success || res.status === 'success' || res.message) {
                setSuccess('OTP sent successfully to your phone number.');
                setTimeout(() => {
                    setSuccess('');
                    setStep(2);
                }, 800);
            } else {
                setError(res.message || 'Failed to send OTP. Please try again.');
            }
        } catch (err) {
            setError(err?.message || 'Failed to send OTP. Please check your phone number and try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // ── OTP digit input handling ───────────────────────────────────────────────
    const handleOtpChange = (index, value) => {
        if (!/^\d*$/.test(value)) return;
        const updated = [...otp];
        updated[index] = value.slice(-1);
        setOtp(updated);
        if (value && index < 5) {
            otpRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (text.length === 6) {
            setOtp(text.split(''));
            otpRefs.current[5]?.focus();
        }
    };

    // ── Step 2: Verify OTP ────────────────────────────────────────────────────
    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        setError('');

        const otpString = otp.join('');
        if (otpString.length !== 6) {
            setError('Please enter the complete 6-digit OTP.');
            return;
        }

        setIsLoading(true);
        try {
            const res = await authService.verifyPhoneOtp(phone, otpString);
            if (res.success || res.status === 'success') {
                setSuccess('OTP verified successfully!');
                setTimeout(() => {
                    setSuccess('');
                    setStep(3);
                }, 800);
            } else {
                setError(res.message || 'Invalid OTP. Please try again.');
            }
        } catch (err) {
            setError(err?.message || 'Invalid or expired OTP. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (resendCountdown > 0) return;
        setError('');
        setIsLoading(true);
        try {
            await authService.sendPhoneOtp(phone);
            setSuccess('OTP resent successfully!');
            startResendTimer();
            setTimeout(() => setSuccess(''), 2000);
        } catch {
            setError('Failed to resend OTP. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // ── Step 3: Reset Password ────────────────────────────────────────────────
    const handleResetPassword = async (e) => {
        e.preventDefault();
        setError('');

        if (newPassword.length < 6) {
            setError('Password must be at least 6 characters long.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match. Please try again.');
            return;
        }

        setIsLoading(true);
        try {
            const res = await authService.resetPassword(phone, newPassword);
            if (res.success || res.status === 'success') {
                setSuccess('Password reset successfully! You can now log in with your new password.');
                setTimeout(() => {
                    handleClose();
                }, 2500);
            } else {
                setError(res.message || 'Failed to reset password. Please try again.');
            }
        } catch (err) {
            setError(err?.message || 'Failed to reset password. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={handleClose}
            />

            {/* Modal Card */}
            <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-fade-in">
                {/* Header Gradient Bar */}
                <div className="h-1.5 bg-gradient-to-r from-primary-blue via-blue-400 to-secondary-blue" />

                <div className="p-8">
                    {/* Close Button */}
                    <button
                        onClick={handleClose}
                        className="absolute top-5 right-5 text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label="Close"
                    >
                        <FaTimes className="w-5 h-5" />
                    </button>

                    {/* Title */}
                    <div className="text-center mb-6">
                        <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-50 rounded-full mb-4">
                            <FaKey className="w-6 h-6 text-primary-blue" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900">Forgot Password?</h2>
                        <p className="text-gray-500 text-sm mt-1">
                            {step === 1 && "Enter your registered phone number to receive an OTP."}
                            {step === 2 && `We've sent a 6-digit OTP to +91 ${phone}`}
                            {step === 3 && "Create your new password below."}
                        </p>
                    </div>

                    {/* Step Indicator */}
                    <StepIndicator currentStep={step} />

                    {/* Error / Success Messages */}
                    {error && (
                        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-start gap-2">
                            <span className="mt-0.5">⚠️</span>
                            <span>{error}</span>
                        </div>
                    )}
                    {success && (
                        <div className="mb-4 px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm flex items-start gap-2">
                            <FaCheckCircle className="mt-0.5 flex-shrink-0" />
                            <span>{success}</span>
                        </div>
                    )}

                    {/* ── STEP 1: Enter Phone ─────────────────────────────────────── */}
                    {step === 1 && (
                        <form onSubmit={handleSendOtp} className="space-y-5">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Phone Number *
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                        <span className="text-gray-500 text-sm font-medium">+91</span>
                                    </div>
                                    <div className="absolute inset-y-0 left-12 w-px bg-gray-200" />
                                    <div className="absolute inset-y-0 left-14 flex items-center pointer-events-none pl-2">
                                        <FaPhone className="text-gray-400 w-4 h-4" />
                                    </div>
                                    <input
                                        type="tel"
                                        value={phone}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                                            setPhone(val);
                                        }}
                                        placeholder="Enter 10-digit phone number"
                                        className="w-full pl-20 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all text-gray-900"
                                        required
                                        maxLength={10}
                                        autoFocus
                                    />
                                </div>
                                <p className="mt-1.5 text-xs text-gray-500">
                                    Use the phone number registered with your account.
                                </p>
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading || phone.length !== 10}
                                className="w-full py-3 px-4 bg-gradient-to-r from-primary-blue to-secondary-blue text-white font-semibold rounded-lg hover:shadow-lg transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
                            >
                                {isLoading ? (
                                    <><FaSpinner className="animate-spin" /> Sending OTP...</>
                                ) : (
                                    'Send OTP'
                                )}
                            </button>
                        </form>
                    )}

                    {/* ── STEP 2: Enter OTP ──────────────────────────────────────── */}
                    {step === 2 && (
                        <form onSubmit={handleVerifyOtp} className="space-y-5">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-4 text-center">
                                    Enter 6-digit OTP
                                </label>
                                <div className="flex justify-center gap-2" onPaste={handleOtpPaste}>
                                    {otp.map((digit, index) => (
                                        <input
                                            key={index}
                                            ref={(el) => (otpRefs.current[index] = el)}
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={1}
                                            value={digit}
                                            onChange={(e) => handleOtpChange(index, e.target.value)}
                                            onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                            className="w-11 h-12 text-center text-xl font-bold border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all text-gray-900 bg-gray-50 focus:bg-white"
                                            autoFocus={index === 0}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Resend OTP */}
                            <p className="text-center text-sm text-gray-500">
                                Didn't receive the OTP?{' '}
                                <button
                                    type="button"
                                    onClick={handleResendOtp}
                                    disabled={resendCountdown > 0 || isLoading}
                                    className={`font-semibold transition-colors ${resendCountdown > 0 || isLoading
                                            ? 'text-gray-400 cursor-not-allowed'
                                            : 'text-primary-blue hover:text-secondary-blue cursor-pointer'
                                        }`}
                                >
                                    {resendCountdown > 0 ? `Resend in ${resendCountdown}s` : 'Resend OTP'}
                                </button>
                            </p>

                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => { setStep(1); setOtp(['', '', '', '', '', '']); setError(''); }}
                                    className="flex-1 py-3 px-4 border-2 border-gray-200 text-gray-600 font-semibold rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-all"
                                >
                                    Back
                                </button>
                                <button
                                    type="submit"
                                    disabled={isLoading || otp.join('').length !== 6}
                                    className="flex-1 py-3 px-4 bg-gradient-to-r from-primary-blue to-secondary-blue text-white font-semibold rounded-lg hover:shadow-lg transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
                                >
                                    {isLoading ? (
                                        <><FaSpinner className="animate-spin" /> Verifying...</>
                                    ) : (
                                        'Verify OTP'
                                    )}
                                </button>
                            </div>
                        </form>
                    )}

                    {/* ── STEP 3: New Password ────────────────────────────────────── */}
                    {step === 3 && (
                        <form onSubmit={handleResetPassword} className="space-y-5">
                            {/* New Password */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    New Password *
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FaLock className="text-gray-400" />
                                    </div>
                                    <input
                                        type={showNewPassword ? 'text' : 'password'}
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="Enter new password"
                                        className="w-full pl-10 pr-12 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all text-gray-900"
                                        required
                                        minLength={6}
                                        autoFocus
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-primary-blue transition-colors"
                                    >
                                        {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                                    </button>
                                </div>
                                <p className="mt-1 text-xs text-gray-500">Minimum 6 characters.</p>
                            </div>

                            {/* Confirm Password */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Confirm New Password *
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FaLock className="text-gray-400" />
                                    </div>
                                    <input
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder="Confirm new password"
                                        className={`w-full pl-10 pr-12 py-3 border-2 rounded-lg focus:ring-2 transition-all text-gray-900 ${confirmPassword && confirmPassword !== newPassword
                                                ? 'border-red-300 focus:ring-red-200 focus:border-red-400'
                                                : confirmPassword && confirmPassword === newPassword
                                                    ? 'border-green-300 focus:ring-green-200 focus:border-green-400'
                                                    : 'border-gray-200 focus:ring-primary-blue focus:border-primary-blue'
                                            }`}
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-primary-blue transition-colors"
                                    >
                                        {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                    </button>
                                </div>
                                {confirmPassword && confirmPassword !== newPassword && (
                                    <p className="mt-1 text-xs text-red-500">Passwords do not match.</p>
                                )}
                                {confirmPassword && confirmPassword === newPassword && (
                                    <p className="mt-1 text-xs text-green-500 flex items-center gap-1">
                                        <FaCheckCircle className="w-3 h-3" /> Passwords match!
                                    </p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading || !newPassword || !confirmPassword}
                                className="w-full py-3 px-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-lg hover:shadow-lg transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
                            >
                                {isLoading ? (
                                    <><FaSpinner className="animate-spin" /> Resetting Password...</>
                                ) : (
                                    '✓ Reset Password'
                                )}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ForgotPasswordModal;
